import React, { Fragment, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPatientDetailsApi } from "../../services/patient.services";
import { UserContext } from "../../context/UserContext";
import PatientMenu from "./PatientMenu";
import DoctorIP from "./DoctorIP";
import NurseIP from "./NurseIP";
import SpeechAndSwallowTherapistIP from "./SpeechAndSwallowTherapistIP";
import RespiratoryTherapistIP from "./RespiratoryTherapistIP";
import NutritionistIP from "./NutritionistIP";
import PhysiotherapistIP from "./PhysiotherapistIP";
import InvestigationsIp from "./InvestigationsIp/InvestigationsIP";
import useQuery from "../../hooks/useQuery";
import ProgressMonitoring from "./ProgressMonitoring/ProgressMonitoring";
import DietChart from "./Diet/DietChart";
import PatientGallery from "./PatientGallery/PatientGallery";
import PsychologistIP from "./PsychologistIP";
import ClinicalPractitionerIP from "./ClinicalPractitionerIP";
import ClinicalPractitionerFormById from "./ClinicalPractitioner/ClinicalPractitionerFormById";
import {
  ADMISSION_TYPES,
  detectAdmissionType,
} from "./ClinicalPractitioner/admissionContext";
import { getAdmissionApi } from "../../services/op.service";
import ReceptionistIp from "./ReceptionistIp/ReceptionistIp";
import NutritionistDiet from "./Diet/NutritionistDiet/NutritionistDiet";
import { getCookie } from "../../helpers/localStorage";
import { Role } from "../../constants/defaultKeys";
import MonitoringCharts from "./MonitoringCharts/MonitoringCharts";
import DischargeSummary from "./Forms/DischargeSummary/DischargeSummary";
import EmrDownload from "./Forms/EmrDownload/EmrDownload";
import ReferralForm from "./FormsIp/ReferralForm";
import DeathSummary from "./FormsIp/DeathSummary";
import DietaryDischargeSummary from "./FormsIp/DietaryDischargeSummary";
import InvestigationReferralForm from "./FormsIp/InvestigationReferralForm";
import ReviewForms from "./FormsIp/ReviewForms/ReviewForms";
import ReceptionistDischargeSummary from "./Forms/DischargeSummary/ReceptionistDischargeSummary";
import NutritionistReassessment from "./FormsIp/NutritionistForms/NutritionistReassessment";
import { FloatButton, Typography } from "antd";
import aiIcon from "../../assets/dashboardIcons/aiIcon.svg";
import RecordModal from "./AiAssistant/RecordModal";
import FloatingRecorder from "./AiAssistant/FloatingRecorder";
import useIsMobile from "../../hooks/useIsMobile";
import useAiFloatClearance from "../../hooks/useAiFloatClearance";
import "./receptionistIpContent.css";
import "./receptionistIpForms.css";
import "./nurseIpContent.css";
import "./doctorIpContent.css";
import "./physioIpContent.css";
import "./nutritionistIpContent.css";
import "./speechIpContent.css";
import "./psychologistIpContent.css";
import "./respiratoryIpContent.css";

const RECEPTIONIST_FORM_TYPES = new Set([
  "discharge-summary",
  "emr-download",
  "referral-form",
  "death-summary",
  "investigation-referral-form",
  "review-forms",
]);

const PatientsIP = () => {
  const navigate = useNavigate();
  const params = useQuery();
  const { userDetails } = useContext(UserContext);
  const userRole = userDetails?.currRole || getCookie(Role);
  const [current, setCurrent] = useState("ia");
  const [patientData, setPatientData] = useState(null);
  const [refresh, setRefresh] = useState(null);
  const refreshPage = () => setRefresh((prev) => !prev);
  const [openModal, setOpenModal] = useState(false);
  const [showFloatingRecorder, setShowFloatingRecorder] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [showTooltip, setShowTooltip] = useState(true);
  const url = new URL(window?.location?.href);

  const role = userRole?.toLowerCase()?.replaceAll("_", "-");
  const isMobile = useIsMobile();
  useAiFloatClearance(userRole === "Doctor");
  const contentSidePad =
    userRole === "Receptionist" ||
    userRole === "Nurse" ||
    userRole === "Doctor" ||
    userRole === "Physiotherapist" ||
    userRole === "Nutritionist" ||
    userRole === "Speech_Swallow_Therapist" ||
    userRole === "Psychologist" ||
    userRole === "Respiratory_Therapist"
      ? undefined
      : isMobile
      ? { paddingLeft: "4px", paddingRight: "4px", paddingBottom: "2px" }
      : { paddingLeft: "24px", paddingRight: "24px", paddingBottom: "2px" };

  const onClick = (e) => {
    setCurrent(e.key);

    url?.searchParams?.set("type", e.key);
    url?.searchParams?.delete("chat");
    navigate(`${document?.location.pathname}?${url.searchParams.toString()}`, {
      replace: true,
    });
  };

  useEffect(() => {
    if (!params?.patient) {
      setPatientData(null);
      return;
    }
    const admissionType = detectAdmissionType(
      window?.location?.pathname,
      window?.location?.search
    );
    if (admissionType === ADMISSION_TYPES.OP) {
      getAdmissionApi(params.patient)
        .then((res) => {
          const data = res?.data || {};
          setPatientData({
            admission: {
              ...data,
              patient: data.patient || {},
              op_id: data.op_id,
            },
          });
        })
        .catch((err) => console.log(err));
      return;
    }
    getPatientDetailsApi(params?.patient)
      .then((res) => setPatientData(res?.data?.data))
      .catch((err) => console.log(err));
  }, [refresh, params?.patient, params?.type]);

  // Auto-hide tooltip after 5 seconds
  useEffect(() => {
    if (userRole === "Doctor" && showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [userRole, showTooltip]);

  const handleProcessComplete = (responseData) => {
    setAiResponse(responseData);
    setShowFloatingRecorder(false);
    setOpenModal(true);
  };
  //maintaining global ai button seperately in medicine,monitoring,investigations,order-sheet because of refresh issue after submitting.

  const hideAiAssistantOnTypes = new Set([
    "medicine",
    "monitoring",
    "investigations",
    "order-sheet",
    "chat",
    "discharge-summary",
    "review-forms",
    "referral-form",
  ]);

  const renderComponent = () => {
    switch (userRole) {
      case "Doctor":
        return (
          <DoctorIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "Nurse":
        return (
          <NurseIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "Speech_Swallow_Therapist":
        return (
          <SpeechAndSwallowTherapistIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "Respiratory_Therapist":
        return (
          <RespiratoryTherapistIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "Nutritionist":
        return (
          <NutritionistIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "Physiotherapist":
        return (
          <PhysiotherapistIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "Receptionist":
        return (
          <ReceptionistIp
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "Psychologist":
        return (
          <PsychologistIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "Clinical_Practitioner":
        return (
          <ClinicalPractitionerIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      default:
        return null;
    }
  };

  const CLINICAL_FORM_PREFIX = "clinical-form-";

  function subComponent() {
    if (
      userRole !== "Clinical_Practitioner" &&
      typeof params.type === "string" &&
      params.type.startsWith(CLINICAL_FORM_PREFIX)
    ) {
      const formId = params.type.slice(CLINICAL_FORM_PREFIX.length);
      return (
        <ClinicalPractitionerFormById
          formId={formId}
          admissionId={params?.patient}
          admissionType={detectAdmissionType(
            window?.location?.pathname,
            window?.location?.search
          )}
        />
      );
    }
    switch (params.type) {
      case "investigations":
        return <InvestigationsIp patientData={patientData?.admission} />;
      case "progress_monitoring":
        return <ProgressMonitoring patient={params?.patient} />;
      case "diet":
        return role === "nurse" ? (
          <DietChart />
        ) : (
          <NutritionistDiet patientData={patientData} />
        );
      case "monitoring-charts":
        return (
          <MonitoringCharts
            patient={params?.patient}
            patientData={patientData}
          />
        );
      case "gallery":
        return <PatientGallery patientData={patientData} />;
      case "discharge-summary":
        return role === "receptionist" || role === "nurse" ? (
          <ReceptionistDischargeSummary patientData={patientData} />
        ) : (
          <DischargeSummary patientData={patientData} />
        );
      case "referral-form":
        return (
          <ReferralForm
            patient={params?.patient}
            patientData={patientData}
            role={role}
          />
        );
      case "nutritionist-reassessment":
        return (
          // <ReassessmentTabs
          //   patient={params?.patient}
          //   patientData={patientData}
          // />
          <NutritionistReassessment
            patient={params?.patient}
            patientData={patientData}
            refreshEmr={refreshPage}
          />
        );
      case "review-forms":
        return (
          <ReviewForms
            patient={params?.patient}
            patientData={patientData}
            role={role}
          />
        );
      case "death-summary":
        return <DeathSummary patientData={patientData} role={role} />;
      case "diet-discharge-summary":
        return <DietaryDischargeSummary patientData={patientData} />;
      case "investigation-referral-form":
        return (
          <InvestigationReferralForm patientData={patientData} role={role} />
        );
      case "emr-download":
        return <EmrDownload />;
      case "doctor":
        return (
          <DoctorIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "nurse":
        return (
          <NurseIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "speech_swallow_therapist":
        return (
          <SpeechAndSwallowTherapistIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "respiratory_therapist":
        return (
          <RespiratoryTherapistIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "nutritionist":
        return (
          <NutritionistIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "physiotherapist":
        return (
          <PhysiotherapistIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      case "psychologist":
        return (
          <PsychologistIP
            patientData={patientData}
            params={params}
            refreshPage={refreshPage}
          />
        );
      default:
        break;
    }
  }

  return (
    <Fragment>
      <PatientMenu
        current={current}
        onClick={onClick}
        userRole={userRole}
        params={params}
        navigate={navigate}
        patientData={patientData}
      />
      <div
        className={[
          "ip-patient-content",
          userRole === "Receptionist" && "receptionist-ip-content",
          userRole === "Nurse" &&
            (params?.type === "discharge-summary"
              ? "receptionist-ip-content"
              : "nurse-ip-content"),
          userRole === "Doctor" && "doctor-ip-content",
          userRole === "Physiotherapist" && "physio-ip-content",
          userRole === "Nutritionist" && "nutritionist-ip-content",
          userRole === "Speech_Swallow_Therapist" && "speech-ip-content",
          userRole === "Psychologist" && "psychologist-ip-content",
          userRole === "Respiratory_Therapist" && "respiratory-ip-content",
        ]
          .filter(Boolean)
          .join(" ")}
        style={contentSidePad}
      >
        {renderComponent()}
        {(userRole === "Receptionist" &&
          RECEPTIONIST_FORM_TYPES.has(params?.type)) ||
        (userRole === "Nurse" && params?.type === "discharge-summary") ? (
          <div key={params.type} className="receptionist-ip-form-view">
            {subComponent()}
          </div>
        ) : (
          (() => {
            const shared = subComponent();
            return shared ? <div key={params.type}>{shared}</div> : null;
          })()
        )}
      </div>

      {userRole === "Doctor" && (
        <>
          {!hideAiAssistantOnTypes.has(params?.type) && (
            <>
              <FloatButton
                shape="circle"
                type="primary"
                className="ai-assistant-float"
                icon={<img src={aiIcon} alt="AI Icon" width={25} height={32} />}
                onClick={() => {
                  setShowFloatingRecorder(true);
                  setShowTooltip(false);
                }}
              />
              {/* Custom Tooltip */}
              {showTooltip && (
                <div className="ai-assistant-tooltip">
                  <Typography.Text style={{ color: "white", fontSize: "13px" }}>
                    Hi! I’m Curengo AI—your voice assistant for clinical orders.
                  </Typography.Text>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Floating Recorder UI */}
      {showFloatingRecorder && (
        <FloatingRecorder
          onProcessComplete={handleProcessComplete}
          onClose={() => {
            setShowFloatingRecorder(false);
          }}
          userDetails={userDetails}
        />
      )}

      {/* Modal with AI Response */}
      {openModal && aiResponse && (
        <RecordModal
          open={openModal}
          setOpen={setOpenModal}
          refreshPage={refreshPage}
          aiResponse={aiResponse}
          startInFormStep={true}
        />
      )}
    </Fragment>
  );
};

export default PatientsIP;
